/**
 * Offline Feedback UI
 *
 * Provides visual feedback to users about their offline status and queued actions.
 * Displays non-intrusive notifications and status indicators.
 *
 * @package DemocraticServices
 * @since 3.1.0
 * @author Democratic Services Team
 *
 * Design Decisions:
 * - Uses accessible notification patterns (ARIA live regions)
 * - Non-blocking UI - notifications auto-dismiss
 * - Clear visual hierarchy for offline status
 * - Matches Democratic Services design system colors
 */

import { getPendingCount } from './offline-queue.js';
import { getOnlineStatus } from './background-sync.js';

// =============================================================================
// CONFIGURATION
// =============================================================================

const UI_CONFIG = {
	// Auto-dismiss delay for notifications (milliseconds)
	notificationDuration: 5000,

	// Animation duration
	animationDuration: 300,

	// Maximum visible notifications
	maxNotifications: 3,

	// Z-index for notifications
	zIndex: 9999,
};

/**
 * Notification types with their styling
 */
const NOTIFICATION_TYPES = {
	offline: {
		icon: '📡',
		bgColor: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
		textColor: '#ffffff',
	},
	online: {
		icon: '✅',
		bgColor: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
		textColor: '#ffffff',
	},
	queued: {
		icon: '💾',
		bgColor: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
		textColor: '#ffffff',
	},
	synced: {
		icon: '✓',
		bgColor: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
		textColor: '#ffffff',
	},
	syncError: {
		icon: '⚠️',
		bgColor: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
		textColor: '#ffffff',
	},
	retrying: {
		icon: '🔄',
		bgColor: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
		textColor: '#ffffff',
	},
	info: {
		icon: 'ℹ️',
		bgColor: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
		textColor: '#ffffff',
	},
};

// =============================================================================
// STATE
// =============================================================================

/**
 * Container element for notifications
 * @type {HTMLElement|null}
 */
let notificationContainer = null;

/**
 * Offline status banner element
 * @type {HTMLElement|null}
 */
let offlineBanner = null;

/**
 * Active notification elements
 * @type {HTMLElement[]}
 */
const activeNotifications = [];

/**
 * Track if already initialized to prevent duplicate event listeners
 * @type {boolean}
 */
let isInitialized = false;

/**
 * Track recent notifications to prevent duplicates
 * @type {Map<string, number>}
 */
const recentNotifications = new Map();

/**
 * Debounce time for duplicate notification prevention (ms)
 */
const NOTIFICATION_DEBOUNCE_MS = 2000;

// =============================================================================
// STYLES
// =============================================================================

/**
 * Inject CSS styles for feedback UI
 */
function injectStyles() {
	if (document.getElementById('offline-feedback-styles')) {
		return;
	}

	const style = document.createElement('style');
	style.id = 'offline-feedback-styles';
	style.textContent = `
    /* ========================================
     * Notification Container
     * ======================================== */
    .offline-notification-container {
      position: fixed;
      bottom: 20px;
      right: 20px;
      z-index: ${UI_CONFIG.zIndex};
      display: flex;
      flex-direction: column;
      gap: 10px;
      pointer-events: none;
      max-width: 400px;
      width: calc(100% - 40px);
    }
    
    @media (max-width: 640px) {
      .offline-notification-container {
        left: 20px;
        right: 20px;
        bottom: 70px; /* Account for mobile navigation */
      }
    }
    
    /* ========================================
     * Individual Notification
     * ======================================== */
    .offline-notification {
      pointer-events: auto;
      padding: 16px 20px;
      border-radius: 12px;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15),
                  0 4px 6px rgba(0, 0, 0, 0.1);
      display: flex;
      align-items: center;
      gap: 12px;
      font-family: 'Inter', system-ui, -apple-system, sans-serif;
      font-size: 14px;
      font-weight: 500;
      animation: slideInRight ${UI_CONFIG.animationDuration}ms ease-out;
      backdrop-filter: blur(10px);
    }
    
    .offline-notification.dismissing {
      animation: slideOutRight ${UI_CONFIG.animationDuration}ms ease-in forwards;
    }
    
    .offline-notification-icon {
      font-size: 20px;
      flex-shrink: 0;
    }
    
    .offline-notification-content {
      flex: 1;
    }
    
    .offline-notification-title {
      font-weight: 600;
      margin-bottom: 2px;
    }
    
    .offline-notification-message {
      opacity: 0.9;
      font-size: 13px;
    }
    
    .offline-notification-dismiss {
      background: rgba(255, 255, 255, 0.2);
      border: none;
      color: inherit;
      padding: 6px 10px;
      border-radius: 6px;
      cursor: pointer;
      font-size: 16px;
      transition: background 0.2s ease;
      flex-shrink: 0;
    }
    
    .offline-notification-dismiss:hover {
      background: rgba(255, 255, 255, 0.3);
    }
    
    .offline-notification-dismiss:focus {
      outline: 2px solid rgba(255, 255, 255, 0.5);
      outline-offset: 2px;
    }
    
    /* ========================================
     * Offline Banner (Persistent)
     * ======================================== */
    .offline-banner {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
      color: white;
      padding: 10px 20px;
      font-family: 'Inter', system-ui, -apple-system, sans-serif;
      font-size: 14px;
      font-weight: 500;
      text-align: center;
      z-index: ${UI_CONFIG.zIndex + 1};
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      animation: slideDown ${UI_CONFIG.animationDuration}ms ease-out;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
    
    .offline-banner.hidden {
      animation: slideUp ${UI_CONFIG.animationDuration}ms ease-in forwards;
    }
    
    .offline-banner-icon {
      font-size: 18px;
    }
    
    .offline-banner-pending {
      background: rgba(255, 255, 255, 0.2);
      padding: 2px 8px;
      border-radius: 12px;
      margin-left: 8px;
    }
    
    /* ========================================
     * Pending Actions Indicator
     * ======================================== */
    .pending-actions-indicator {
      position: fixed;
      bottom: 80px;
      right: 20px;
      background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
      color: white;
      padding: 12px 16px;
      border-radius: 50px;
      font-family: 'Inter', system-ui, -apple-system, sans-serif;
      font-size: 13px;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 8px;
      box-shadow: 0 4px 20px rgba(245, 158, 11, 0.4);
      z-index: ${UI_CONFIG.zIndex - 1};
      cursor: pointer;
      transition: transform 0.2s ease, box-shadow 0.2s ease;
    }
    
    .pending-actions-indicator:hover {
      transform: scale(1.05);
      box-shadow: 0 6px 25px rgba(245, 158, 11, 0.5);
    }
    
    .pending-actions-count {
      background: rgba(255, 255, 255, 0.3);
      padding: 2px 8px;
      border-radius: 10px;
    }
    
    /* ========================================
     * Animations
     * ======================================== */
    @keyframes slideInRight {
      from {
        opacity: 0;
        transform: translateX(100%);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }
    
    @keyframes slideOutRight {
      from {
        opacity: 1;
        transform: translateX(0);
      }
      to {
        opacity: 0;
        transform: translateX(100%);
      }
    }
    
    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-100%);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    @keyframes slideUp {
      from {
        opacity: 1;
        transform: translateY(0);
      }
      to {
        opacity: 0;
        transform: translateY(-100%);
      }
    }
    
    /* ========================================
     * Reduced Motion
     * ======================================== */
    @media (prefers-reduced-motion: reduce) {
      .offline-notification,
      .offline-notification.dismissing,
      .offline-banner,
      .offline-banner.hidden {
        animation-duration: 0.01ms !important;
      }
    }
  `;

	document.head.appendChild(style);
}

// =============================================================================
// NOTIFICATION CONTAINER
// =============================================================================

/**
 * Create or get the notification container
 *
 * @returns {HTMLElement} The container element
 */
function getNotificationContainer() {
	if (notificationContainer && document.body.contains(notificationContainer)) {
		return notificationContainer;
	}

	notificationContainer = document.createElement('div');
	notificationContainer.className = 'offline-notification-container';
	notificationContainer.setAttribute('role', 'region');
	notificationContainer.setAttribute('aria-label', 'Notifications');
	notificationContainer.setAttribute('aria-live', 'polite');

	document.body.appendChild(notificationContainer);

	return notificationContainer;
}

// =============================================================================
// NOTIFICATIONS
// =============================================================================

/**
 * Show a notification
 *
 * @param {Object} options - Notification options
 * @param {string} options.type - Notification type (from NOTIFICATION_TYPES)
 * @param {string} options.title - Notification title
 * @param {string} options.message - Notification message
 * @param {number} [options.duration] - Auto-dismiss duration (0 for permanent)
 * @returns {HTMLElement|null} The notification element, or null if deduplicated
 */
export function showNotification(options) {
	const {
		type = 'info',
		title,
		message,
		duration = UI_CONFIG.notificationDuration,
	} = options;

	// Create a unique key for this notification to prevent duplicates
	const notificationKey = `${type}:${title || ''}:${message || ''}`;
	const now = Date.now();

	// Check if we've shown this notification recently
	if (recentNotifications.has(notificationKey)) {
		const lastShown = recentNotifications.get(notificationKey);
		if (now - lastShown < NOTIFICATION_DEBOUNCE_MS) {
			return null;
		}
	}

	// Track this notification
	recentNotifications.set(notificationKey, now);

	// Clean up old entries from the map periodically
	if (recentNotifications.size > 20) {
		for (const [key, timestamp] of recentNotifications.entries()) {
			if (now - timestamp > NOTIFICATION_DEBOUNCE_MS * 2) {
				recentNotifications.delete(key);
			}
		}
	}

	const typeConfig = NOTIFICATION_TYPES[type] || NOTIFICATION_TYPES.info;
	const container = getNotificationContainer();

	// Limit active notifications
	while (activeNotifications.length >= UI_CONFIG.maxNotifications) {
		const oldest = activeNotifications.shift();
		dismissNotification(oldest, true);
	}

	// Create notification element
	const notification = document.createElement('div');
	notification.className = 'offline-notification';
	notification.style.background = typeConfig.bgColor;
	notification.style.color = typeConfig.textColor;
	notification.setAttribute('role', 'alert');

	notification.innerHTML = `
    <span class="offline-notification-icon" aria-hidden="true">${typeConfig.icon}</span>
    <div class="offline-notification-content">
      ${title ? `<div class="offline-notification-title">${escapeHtml(title)}</div>` : ''}
      ${message ? `<div class="offline-notification-message">${escapeHtml(message)}</div>` : ''}
    </div>
    <button type="button" class="offline-notification-dismiss" aria-label="Dismiss notification">
      ✕
    </button>
  `;

	// Dismiss button handler
	const dismissBtn = notification.querySelector(
		'.offline-notification-dismiss',
	);
	dismissBtn?.addEventListener('click', () =>
		dismissNotification(notification),
	);

	// Add to container
	container.appendChild(notification);
	activeNotifications.push(notification);

	// Auto-dismiss
	if (duration > 0) {
		setTimeout(() => {
			dismissNotification(notification);
		}, duration);
	}

	return notification;
}

/**
 * Dismiss a notification
 *
 * @param {HTMLElement} notification - The notification to dismiss
 * @param {boolean} immediate - Skip animation
 */
function dismissNotification(notification, immediate = false) {
	if (!notification || !notification.parentElement) {
		return;
	}

	// Remove from active list
	const index = activeNotifications.indexOf(notification);
	if (index > -1) {
		activeNotifications.splice(index, 1);
	}

	if (immediate) {
		notification.remove();
		return;
	}

	// Animate out
	notification.classList.add('dismissing');
	setTimeout(() => {
		notification.remove();
	}, UI_CONFIG.animationDuration);
}

// =============================================================================
// OFFLINE BANNER
// =============================================================================

/**
 * Show the offline banner
 */
export async function showOfflineBanner() {
	if (offlineBanner && document.body.contains(offlineBanner)) {
		return;
	}

	const pendingCount = await getPendingCount();

	offlineBanner = document.createElement('div');
	offlineBanner.className = 'offline-banner';
	offlineBanner.setAttribute('role', 'status');
	offlineBanner.setAttribute('aria-live', 'assertive');

	offlineBanner.innerHTML = `
    <span class="offline-banner-icon" aria-hidden="true">📡</span>
    <span>You are currently offline</span>
    ${
	pendingCount > 0
		? `
      <span class="offline-banner-pending">
        ${pendingCount} action${pendingCount > 1 ? 's' : ''} waiting to sync
      </span>
    `
		: ''
}
  `;

	document.body.appendChild(offlineBanner);

	// Adjust body padding to account for banner
	document.body.style.paddingTop = `${offlineBanner.offsetHeight}px`;
}

/**
 * Hide the offline banner
 */
export function hideOfflineBanner() {
	if (!offlineBanner) {
		return;
	}

	offlineBanner.classList.add('hidden');

	setTimeout(() => {
		offlineBanner?.remove();
		offlineBanner = null;
		document.body.style.paddingTop = '';
	}, UI_CONFIG.animationDuration);
}

// =============================================================================
// PENDING ACTIONS INDICATOR
// =============================================================================

/**
 * Pending actions indicator element
 * @type {HTMLElement|null}
 */
let pendingIndicator = null;

/**
 * Update the pending actions indicator
 */
export async function updatePendingIndicator() {
	const pendingCount = await getPendingCount();

	if (pendingCount === 0) {
		hidePendingIndicator();
		return;
	}

	if (!pendingIndicator) {
		pendingIndicator = document.createElement('div');
		pendingIndicator.className = 'pending-actions-indicator';
		pendingIndicator.setAttribute('role', 'status');
		pendingIndicator.setAttribute('aria-live', 'polite');
		document.body.appendChild(pendingIndicator);

		// Click to trigger manual sync
		pendingIndicator.addEventListener('click', () => {
			dispatchEvent(new CustomEvent('offlineFeedback:syncRequested'));
		});
	}

	pendingIndicator.innerHTML = `
    <span aria-hidden="true">💾</span>
    <span>Pending</span>
    <span class="pending-actions-count">${pendingCount}</span>
  `;
	pendingIndicator.setAttribute(
		'aria-label',
		`${pendingCount} actions waiting to sync`,
	);
}

/**
 * Hide the pending actions indicator
 */
function hidePendingIndicator() {
	pendingIndicator?.remove();
	pendingIndicator = null;
}

// =============================================================================
// CONVENIENCE FUNCTIONS
// =============================================================================

/**
 * Show offline notification
 */
export function notifyOffline() {
	showNotification({
		type: 'offline',
		title: "You're offline",
		message: "Changes will be saved and synced when you're back online.",
	});
	showOfflineBanner();
}

/**
 * Show online notification
 */
export function notifyOnline() {
	showNotification({
		type: 'online',
		title: 'Back online',
		message: 'Syncing your saved changes...',
	});
	hideOfflineBanner();
}

/**
 * Show action queued notification
 *
 * @param {string} actionType - Type of action (petition, consultation)
 */
export function notifyActionQueued(actionType = 'action') {
	// Add verification note for petitions
	const isPetition = actionType.toLowerCase().includes('petition');
	const message = isPetition
		? `Your ${actionType} will be submitted when you're back online. Check your email to verify.`
		: `Your ${actionType} will be submitted when you're back online.`;

	showNotification({
		type: 'queued',
		title: 'Saved for later',
		message,
	});
	updatePendingIndicator();
}

/**
 * Show action synced notification
 *
 * @param {string} actionType - Type of action
 */
export function notifyActionSynced(actionType = 'action') {
	// Add verification note for petitions
	const isPetition = actionType.toLowerCase().includes('petition');
	const title = isPetition ? 'Signature submitted' : 'Synced successfully';
	const message = isPetition
		? 'Please check your email to verify your signature before it counts.'
		: `Your ${actionType} has been submitted.`;

	showNotification({
		type: 'synced',
		title,
		message,
		duration: isPetition ? 6000 : 3000, // Longer duration for important verification message
	});
	updatePendingIndicator();
}

/**
 * Show sync error notification
 *
 * @param {string} message - Error message
 */
export function notifySyncError(message = 'Please try again later.') {
	showNotification({
		type: 'syncError',
		title: 'Sync failed',
		message: message,
		duration: 7000,
	});
}

/**
 * Show sync complete notification
 *
 * @param {Object} results - Sync results
 */
export function notifySyncComplete(results) {
	if (results.success > 0) {
		showNotification({
			type: 'synced',
			title: 'Sync complete',
			message: `${results.success} of ${results.total} actions synced successfully.`,
			duration: 4000,
		});
	}

	if (results.failed > 0) {
		showNotification({
			type: 'syncError',
			title: 'Some actions failed',
			message: `${results.failed} action${results.failed > 1 ? 's' : ''} could not be synced.`,
			duration: 6000,
		});
	}

	updatePendingIndicator();
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Escape HTML to prevent XSS
 *
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
function escapeHtml(str) {
	const div = document.createElement('div');
	div.textContent = str;
	return div.innerHTML;
}

/**
 * Get human-readable label for action types
 *
 * @param {string} type - Action type from ACTION_TYPES
 * @returns {string} Human-readable label
 */
function getActionTypeLabel(type) {
	if (!type) return 'action';

	if (type.includes('petition_signature') || type.includes('sign_petition')) {
		return 'petition signature';
	}
	if (type.includes('petition_create') || type.includes('create_petition')) {
		return 'petition';
	}
	if (type.includes('consultation')) {
		return 'consultation response';
	}
	if (type.includes('newsletter')) {
		return 'newsletter subscription';
	}
	if (type.includes('vote')) {
		return 'vote';
	}
	if (type.includes('call_in')) {
		return 'call-in request';
	}

	return 'action';
}

// =============================================================================
// EVENT LISTENERS
// =============================================================================

/**
 * Event handler references for cleanup/prevention of duplicates
 */
const eventHandlers = {
	online: null,
	offline: null,
	actionQueued: null,
	syncCompleted: null,
	actionSynced: null,
	actionFailed: null,
	actionRetrying: null,
	deferredSyncCompleted: null,
};

/**
 * Set up event listeners for sync and queue events
 * Uses named handlers to prevent duplicate listeners
 */
function setupEventListeners() {
	// Remove any existing listeners first to prevent duplicates
	if (eventHandlers.online) {
		window.removeEventListener('backgroundSync:online', eventHandlers.online);
	}
	if (eventHandlers.offline) {
		window.removeEventListener('backgroundSync:offline', eventHandlers.offline);
	}
	if (eventHandlers.actionQueued) {
		window.removeEventListener(
			'offlineQueue:actionQueued',
			eventHandlers.actionQueued,
		);
	}
	if (eventHandlers.syncCompleted) {
		window.removeEventListener(
			'backgroundSync:syncCompleted',
			eventHandlers.syncCompleted,
		);
	}
	if (eventHandlers.actionSynced) {
		window.removeEventListener(
			'backgroundSync:actionSynced',
			eventHandlers.actionSynced,
		);
	}
	if (eventHandlers.actionFailed) {
		window.removeEventListener(
			'backgroundSync:actionFailed',
			eventHandlers.actionFailed,
		);
	}
	if (eventHandlers.actionRetrying) {
		window.removeEventListener(
			'backgroundSync:actionRetrying',
			eventHandlers.actionRetrying,
		);
	}
	if (eventHandlers.deferredSyncCompleted) {
		window.removeEventListener(
			'backgroundSync:deferredSyncCompleted',
			eventHandlers.deferredSyncCompleted,
		);
	}

	// Create named handlers
	eventHandlers.online = () => {
		notifyOnline();
	};

	eventHandlers.offline = () => {
		notifyOffline();
	};

	eventHandlers.actionQueued = (event) => {
		const { action } = event.detail || {};
		const type = getActionTypeLabel(action?.type);
		notifyActionQueued(type);
	};

	eventHandlers.syncCompleted = (event) => {
		notifySyncComplete(event.detail);
	};

	eventHandlers.actionSynced = (event) => {
		const { success, type } = event.detail || {};
		if (success) {
			const actionType = getActionTypeLabel(type);
			notifyActionSynced(actionType);
		} else {
			updatePendingIndicator();
		}
	};

	eventHandlers.actionFailed = (event) => {
		const { error, isValidationError } = event.detail || {};
		if (isValidationError) {
			showNotification({
				type: 'syncError',
				title: 'Submission rejected',
				message: error || 'The submission was rejected by the server.',
				duration: 8000,
			});
		} else {
			notifySyncError(
				error || 'The action could not be synced. It will be retried later.',
			);
		}
		updatePendingIndicator();
	};

	eventHandlers.actionRetrying = (event) => {
		const { type, retryNumber, retryDelay } = event.detail || {};
		const actionType = getActionTypeLabel(type);
		showNotification({
			type: 'retrying',
			title: 'Retrying sync...',
			message: `Attempt ${retryNumber} for ${actionType} in ${retryDelay}s`,
			duration: Math.min(retryDelay * 1000, 5000),
		});
	};

	eventHandlers.deferredSyncCompleted = (event) => {
		const { count } = event.detail || {};
		if (count > 0) {
			showNotification({
				type: 'synced',
				title: 'Offline actions synced',
				message: `${count} item${count > 1 ? 's' : ''} synced while you were away.`,
				duration: 6000,
			});
		}
	};

	// Register event listeners
	window.addEventListener('backgroundSync:online', eventHandlers.online);
	window.addEventListener('backgroundSync:offline', eventHandlers.offline);
	window.addEventListener(
		'offlineQueue:actionQueued',
		eventHandlers.actionQueued,
	);
	window.addEventListener(
		'backgroundSync:syncCompleted',
		eventHandlers.syncCompleted,
	);
	window.addEventListener(
		'backgroundSync:actionSynced',
		eventHandlers.actionSynced,
	);
	window.addEventListener(
		'backgroundSync:actionFailed',
		eventHandlers.actionFailed,
	);
	window.addEventListener(
		'backgroundSync:actionRetrying',
		eventHandlers.actionRetrying,
	);
	window.addEventListener(
		'backgroundSync:deferredSyncCompleted',
		eventHandlers.deferredSyncCompleted,
	);
}

// =============================================================================
// INITIALIZATION
// =============================================================================

/**
 * Initialize the offline feedback UI
 */
export function initOfflineFeedback() {
	// Prevent double initialization
	if (isInitialized) {
		return;
	}
	isInitialized = true;

	// Inject styles
	injectStyles();

	// Set up event listeners
	setupEventListeners();

	// Check initial online status
	if (!getOnlineStatus()) {
		showOfflineBanner();
	}

	// Update pending indicator
	updatePendingIndicator();

	// Offline feedback initialized (silent)
}

// Auto-initialize if in browser environment
if (typeof window !== 'undefined') {
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initOfflineFeedback);
	} else {
		initOfflineFeedback();
	}
}
